import classNames from 'classnames';
import React from 'react';

import {
  EmployeeLicenseStatus,
  EmployeeLicenseWarning,
  Enum,
  Language,
  States,
} from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { EmployeeLicenseSummary } from '@vestahealthcare/common/models';
import { UNKNOWN_LANG } from '@vestahealthcare/common/models/EmployeeLicenseSummaryLanguage';
import { getFilterItem } from '@vestahealthcare/common/utils/filter';

import { FilterItem } from 'styleguide-v2';

import RecursiveTable, {
  RecursiveTableData,
} from 'dash/src/components/RecursiveTable';
import { PodReportingStyles } from 'dash/src/pages/Clinical/PodReporting/PodReportingStyles';
import { EmployeeParams } from 'dash/src/services/EmployeeServices';

import { COMPACT, UNKNOWN } from './EmployeeLicenseEdit/EmployeLicensesEdit';
import { EMPLOYEE_TABS_LICENSE_DETAILS } from './types';

type KeyGetEmployeeParams = keyof EmployeeParams;

type Props = {
  employeeLicenseOverview?: EmployeeLicenseSummary;
  onChangeFilters: (
    filters: {
      [x in KeyGetEmployeeParams]?: FilterItem;
    },
  ) => void;
  onChangeSelectedTab: (tab: string) => void;
};

export const EmployeesLicenseOverviewPage = ({
  employeeLicenseOverview,
  onChangeFilters,
  onChangeSelectedTab,
}: Props) => {
  const styles = PodReportingStyles();
  return (
    <div className={classNames('grid-wrapper', styles.borderContainer)}>
      <div className="grid-span-6" style={{ paddingLeft: '4rem' }}>
        <div className="flex gap">
          <h2 className={styles.subtitle}>
            {translate('employees.warningCounts')}
          </h2>
        </div>
        <RecursiveTable
          data={(employeeLicenseOverview?.warnings || []).map((warning) => ({
            name: warning.warning.value,
            column1: warning.total,
          }))}
          header={translate('employees.table.warning')}
          getItemLabel={(item: RecursiveTableData) =>
            EmployeeLicenseWarning.byKey[item.name]?.toString() || item.name
          }
          getItemOnClick={(item) => {
            const warning = EmployeeLicenseWarning.byKey[item.name];
            if (warning) {
              return () => {
                onChangeFilters({
                  licenseWarning: getFilterItem(
                    translate('employees.filters.licenseWarning'),
                    Enum.toSelectable([warning])[0],
                  ),
                });
                onChangeSelectedTab(EMPLOYEE_TABS_LICENSE_DETAILS);
              };
            }
          }}
        />
      </div>
      <div className="grid-span-6">
        <div className="flex gap">
          <h2 className={styles.subtitle}>
            {translate('employees.stateCounts')}
          </h2>
        </div>
        <RecursiveTable
          data={(employeeLicenseOverview?.states || []).map((state) => ({
            name: state.state?.valueOf(),
            column1: state.active,
            column2: state.applicationInProgress,
            column3: state.pending,
            column4: state.expired,
            subtotals: state.languages.map((language) => ({
              name: language.language?.valueOf(),
              column1: language.active,
              column2: language.applicationInProgress,
              column3: language.pending,
              column4: language.expired,
            })),
          }))}
          getItemClassName={(item: RecursiveTableData) =>
            States.byKey[item.name]?.toString() ||
            Language.byKey[item.name]?.toString()
              ? ''
              : 'semi-bold'
          }
          getItemLabel={(item: RecursiveTableData) =>
            States.byKey[item.name]?.toString() ||
            Language.byKey[item.name]?.toString() ||
            item.name
          }
          getItemOnClick={(item, parentItem, column) => {
            const state =
              States.byKey[parentItem ? parentItem.name : item.name];
            // eslint-disable-next-line no-nested-ternary
            const language = parentItem
              ? item.name === UNKNOWN_LANG
                ? 'UNKNOWN'
                : Language.byKey[item.name]
              : undefined;
            let status = [] as EmployeeLicenseStatus[];
            if (column === 'column1') {
              status = [EmployeeLicenseStatus.ACTIVE];
            } else if (column === 'column2') {
              status = [EmployeeLicenseStatus.APPLICATION_IN_PROGRESS];
            } else if (column === 'column3') {
              status = [
                EmployeeLicenseStatus.APPLICATION_SUBMITTED,
                EmployeeLicenseStatus.ENDORSEMENT_SUBMITTED,
              ];
            } else if (column === 'column4') {
              status = [EmployeeLicenseStatus.EXPIRED];
            }
            if (status.length) {
              return () => {
                onChangeFilters({
                  language: language
                    ? getFilterItem(
                        translate('employees.filters.language'),
                        language === 'UNKNOWN'
                          ? [{ ...UNKNOWN, label: 'Unknown' }]
                          : Enum.toSelectable([language]),
                      )
                    : undefined,
                  licenseStatus: getFilterItem(
                    translate('employees.filters.licenseStatus'),
                    Enum.toSelectable(status),
                  ),
                  state: state
                    ? getFilterItem(
                        translate('employees.filters.state'),
                        Enum.toSelectable([state]),
                      )
                    : getFilterItem(translate('employees.filters.state'), [
                        { ...COMPACT, label: 'Compact' },
                      ]),
                });
                onChangeSelectedTab(EMPLOYEE_TABS_LICENSE_DETAILS);
              };
            }
          }}
          header={translate('employees.table.state')}
          header1={EmployeeLicenseStatus.ACTIVE.toString()}
          header2={translate('employees.table.appInProgress')}
          header2Size={125}
          header3={translate('employees.table.pending')}
          header4={EmployeeLicenseStatus.EXPIRED.toString()}
        />
      </div>
    </div>
  );
};
